/**
 * followUpScales.js — s128 (Dr. Rami 2026-11-01; canvas "Orella Diagnostic Scales" board D, D1 + D2; copy
 * claude/ORELLA_SCALES_COPY_v1.md §4 APPROVED "as written"). PURE.
 *
 * The clinician confirms a scale as a management order in Clinical Support (Clinical Panel 243.347.0):
 * followUp.ordersPlaced.scales = [{ scale, dueInDays, baseline: { score, max, at }, dx, inApp }].
 * The follow-up report asks every confirmed `inApp` scale once, after the voice memo and before the bridge questions,
 * then shows baseline → today. The answer is sent with the report and stored on the follow-up ENCOUNTER
 * (pre_visit_report.scales, API v243.410.0, his pick "On the encounter"), never over the Detect baseline.
 *
 * A scale's definition: data/scales.json (NLHQ, TNSS) first, else the catalog's scored-scale question of that name
 * (PHQ-9, GAD-7, ASRS-6). An order whose scale the app cannot draw is not asked (never a blank card).
 */
import { scaleScore, scaleBand, scaleTotalText, parseScale } from '../components/describe/ScoredScale'

/** Every scored-scale question in a catalog, by scale name (first wins). */
export function catalogScales(catalog) {
  const out = {}
  const walk = (o) => {
    if (Array.isArray(o)) { o.forEach(walk); return }
    if (!o || typeof o !== 'object') return
    if (o.type === 'scored-scale' && o.scale && Array.isArray(o.items) && o.items.length && !out[o.scale]) out[o.scale] = o
    Object.values(o).forEach(walk)
  }
  walk(catalog)
  return out
}

/** The confirmed in-app scales to ask: [{ order, def }]. One per scale name, in the clinician's order. */
export function followUpScaleOrders(orders, library, catalog) {
  const free = (library && library.scales) || {}
  const cat = catalogScales(catalog)
  const seen = new Set()
  const out = []
  for (const o of Array.isArray(orders) ? orders : []) {
    if (!o || typeof o !== 'object' || o.inApp !== true) continue
    const name = String(o.scale || '').trim()
    if (!name || seen.has(name)) continue
    const def = free[name] || cat[name]
    if (!def) continue
    seen.add(name)
    out.push({ order: o, def })
  }
  return out
}

/** True when every item of the scale has an answer. */
export const scaleComplete = (def, answer) => {
  const s = parseScale(answer, def)
  return s.length > 0 && s.every((v) => v !== null)
}

/** The score of an answer line, or null when it is not complete. */
export const scaleAnswerScore = (def, answer) => (scaleComplete(def, answer) ? scaleScore(def, parseScale(answer, def)) : null)

/**
 * Baseline → today. { kind: 'better' | 'worse' | 'same', n } where n is the points moved, in the scale's own
 * decimals. Direction follows the scale (`higherIsBetter`, NLHQ); otherwise lower is better (TNSS, PHQ-9, GAD-7).
 * null when either score is missing.
 */
export function scaleChange(def, baselineScore, nowScore) {
  const b = Number(baselineScore)
  const n = Number(nowScore)
  if (baselineScore === null || baselineScore === undefined || nowScore === null || nowScore === undefined) return null
  if (!Number.isFinite(b) || !Number.isFinite(n)) return null
  const k = 10 ** (Number.isFinite(def?.decimals) ? def.decimals : 0)
  const diff = Math.round((n - b) * k) / k
  if (diff === 0) return { kind: 'same', n: 0 }
  const better = def?.higherIsBetter === true ? diff > 0 : diff < 0
  return { kind: better ? 'better' : 'worse', n: scaleTotalText(def, Math.abs(diff)) }
}

/** The stored entry for pre_visit_report.scales. */
export function followUpScaleEntry(def, order, answer, at = new Date().toISOString()) {
  const scores = parseScale(answer, def)
  const total = scaleScore(def, scores)
  const band = scaleBand(def, total)
  const base = order && order.baseline && typeof order.baseline === 'object' ? order.baseline : null
  return {
    scale: def.scale, title: def.title || def.scale, answer, score: total, max: def.max,
    band: band ? band.label : '', dx: (order && order.dx) || '', at,
    ...(base ? { baseline: { score: base.score, max: base.max, at: base.at || '' } } : {}),
  }
}
